// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/Navbar.css';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { path: '/', label: 'Home', icon: '🏠' },
        { path: '/services', label: 'Services', icon: '✨' },
        { path: '/AboutUs', label: 'About', icon: 'ℹ️' },
        { path: '/contact', label: 'Contact', icon: '📧' }
    ];

    const navVariants = {
        hidden: { y: -100, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 20,
                mass: 1
            }
        }
    };

    const itemVariants = {
        hidden: { y: -20, opacity: 0 },
        visible: i => ({
            y: 0,
            opacity: 1,
            transition: {
                delay: i * 0.1,
                type: "spring",
                stiffness: 100
            }
        })
    };

    const mobileMenuVariants = {
        hidden: { x: '100%', opacity: 0 },
        visible: {
            x: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 80,
                damping: 20
            }
        },
        exit: {
            x: '100%',
            opacity: 0,
            transition: {
                type: "spring",
                stiffness: 80,
                damping: 20
            }
        }
    };

    return (
        <motion.nav
            className={`navbar ${scrolled ? 'scrolled' : ''}`}
            variants={navVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="nav-container">
                <motion.div
                    className="nav-logo"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <Link to="/">
                        <motion.img
                            src="/Assets/image/excellent-travels-logo.png"
                            alt="Excellent Travels Logo"
                            className="custom-logo"
                        />
                        <motion.span
                            className="logo-text"
                            whileHover={{
                                scale: 1.05,
                                transition: { duration: 0.2 }
                            }}
                        >
                            Excellent Travels
                        </motion.span>
                    </Link>
                </motion.div>

                <div className="nav-links">
                    {navItems.map((item, i) => (
                        <motion.div
                            key={item.path}
                            custom={i}
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover={{ scale: 1.1 }}
                            className="nav-item"
                        >
                            <Link
                                to={item.path}
                                className={location.pathname === item.path ? 'active' : ''}
                            >
                                <span className="nav-icon">{item.icon}</span>
                                {item.label}
                            </Link>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    className={`mobile-menu-btn ${isOpen ? 'menu-open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    <div className={`hamburger ${isOpen ? 'open' : ''}`}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </motion.div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="mobile-menu"
                            variants={mobileMenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            {navItems.map((item, i) => (
                                <motion.div
                                    key={item.path}
                                    custom={i}
                                    variants={itemVariants}
                                    initial="hidden"
                                    animate="visible"
                                    whileHover={{ scale: 1.05, x: 10 }}
                                >
                                    <Link
                                        to={item.path}
                                        className={location.pathname === item.path ? 'active' : ''}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <span className="nav-icon">{item.icon}</span>
                                        {item.label}
                                    </Link>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.nav>
    );
};

export default Navbar;